// @ts-nocheck
import {
  Alert,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import {
  TextSpeaker,
  SpeechRecognizer,
  SpeechEnhancedTextarea,
  SpeechEnhancedHtmlEditor,
  SpeechCommandBar,
} from '@/components/advanced/speech';

/**
 * Demo "documented stub" showcase for the SDK / mic-bound speech components.
 *
 * Every component in `@/components/advanced/speech` calls a browser speech hook
 * on mount (`useTextToSpeech` / `useSpeechRecognition`) and reads the global
 * preferences store. They access `window.speechSynthesis` / `SpeechRecognition`
 * (browser microphone + Web Speech APIs) and persist settings to encrypted
 * storage. Because they are NOT self-contained, none are rendered live here —
 * each is documented as a doc card only. This keeps the page crash-free at both
 * import and render time regardless of browser capabilities.
 */
export default function SpeechPage() {
  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap="xs">
        <Title order={2}>Speech (TextSpeaker, SpeechRecognizer, voice-enhanced inputs)</Title>
        <Text c="dimmed" size="sm">
          Text-to-speech, speech-to-text dictation, and voice-enhanced inputs built on the Web
          Speech APIs with a pluggable engine router (browser, Whisper, Google, IBM, Voicegain).
          These components bind to the browser microphone and speech synthesis on mount, so they
          are documented here as stubs rather than rendered live.
        </Text>
      </Stack>

      {/* Why doc-only */}
      <Alert
        variant="light"
        color="blue"
        title="Documentation-only showcase"
        icon={<ThemeIcon variant="light" color="blue" radius="xl"><span aria-hidden>i</span></ThemeIcon>}
      >
        <Text size="sm">
          Each component below calls a speech hook (<Code>useTextToSpeech</Code> /{' '}
          <Code>useSpeechRecognition</Code>) on mount and touches{' '}
          <Code>window.speechSynthesis</Code> / <Code>SpeechRecognition</Code>. They require{' '}
          <strong>browser microphone / speech APIs</strong> and the app preferences store, so they
          are shown as documented stubs. Drop them into a real screen to use them live; they
          gracefully degrade to a &quot;not supported&quot; message where the browser lacks the APIs.
        </Text>
      </Alert>

      {/* TextSpeaker */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Title order={4}>TextSpeaker</Title>
              <Badge color="grape" variant="light">
                browser microphone / speech APIs
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Read-aloud control bar for a fixed block of text: Speak / Pause / Stop plus optional
              language, voice, and speed selectors. Backed by the browser speech synthesizer.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • text — string — required (the content to read aloud)
              <br />
              • compact — boolean — optional (default false; tighter spacing / smaller buttons)
              <br />
              • allowRate — boolean — optional (default true; show the speed slider)
              <br />
              • allowVoiceSelect — boolean — optional (default true; show the voice picker)
              <br />
              • allowLanguageSelect — boolean — optional (default true; show the language picker)
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { TextSpeaker } from '@/components/advanced/speech';

<TextSpeaker
  text="Welcome to CCMS. This summary will be read aloud."
  allowRate
  allowVoiceSelect
  allowLanguageSelect
/>`}
            </Code>
          </Stack>

          <Alert variant="light" color="grape" title="Needs to run live">
            <Text size="xs">
              Calls <Code>useTextToSpeech()</Code> on mount and uses{' '}
              <Code>window.speechSynthesis</Code> (browser microphone / speech APIs). Not rendered
              live here — doc only.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* SpeechRecognizer */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Title order={4}>SpeechRecognizer</Title>
              <Badge color="grape" variant="light">
                browser microphone / speech APIs
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Microphone dictation control: start/stop listening, choose language, toggle continuous
              and interim results, and stream the live transcript back via a callback.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • onTranscript — (text: string) =&gt; void — optional (fires as text is recognized)
              <br />
              • showTranscript — boolean — optional (default true; show the read-only transcript box)
              <br />
              • compact — boolean — optional (default false; tighter layout)
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { SpeechRecognizer } from '@/components/advanced/speech';

<SpeechRecognizer
  showTranscript
  onTranscript={(text) => console.log('Heard:', text)}
/>`}
            </Code>
          </Stack>

          <Alert variant="light" color="grape" title="Needs to run live">
            <Text size="xs">
              Calls <Code>useSpeechRecognition()</Code> on mount, which constructs a{' '}
              <Code>SpeechRecognition</Code> instance and requests the microphone (browser
              microphone / speech APIs). Not rendered live here — doc only.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* SpeechEnhancedTextarea */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Title order={4}>SpeechEnhancedTextarea</Title>
              <Badge color="grape" variant="light">
                browser microphone / speech APIs
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              A plain-text textarea wired for voice: dictate to append spoken text, read the field
              aloud, pick voice/language/speed, and select the preferred speech engine. Works as a
              controlled or uncontrolled input.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • value — string — optional (controlled value)
              <br />
              • defaultValue — string — optional (default &apos;&apos;; uncontrolled initial value)
              <br />
              • onChange — (value: string) =&gt; void — optional
              <br />
              • label — string — optional
              <br />
              • placeholder — string — optional
              <br />
              • minRows — number — optional (default 4)
              <br />
              • compact — boolean — optional (default false)
              <br />
              • disabled — boolean — optional (default false)
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { SpeechEnhancedTextarea } from '@/components/advanced/speech';

const [notes, setNotes] = useState('');

<SpeechEnhancedTextarea
  label="Meeting notes"
  placeholder="Type or dictate your notes..."
  value={notes}
  onChange={setNotes}
  minRows={4}
/>`}
            </Code>
          </Stack>

          <Alert variant="light" color="grape" title="Needs to run live">
            <Text size="xs">
              Calls both <Code>useTextToSpeech()</Code> and <Code>useSpeechRecognition()</Code> on
              mount and resolves a speech engine asynchronously (browser microphone / speech APIs).
              Not rendered live here — doc only.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* SpeechEnhancedHtmlEditor */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Title order={4}>SpeechEnhancedHtmlEditor</Title>
              <Badge color="grape" variant="light">
                browser microphone / speech APIs
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              A rich-text (HTML) editor with the same voice toolbar: dictate paragraphs into the
              document, speak the current selection or the whole document, and choose voice,
              language, speed, and engine.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • value — string (HTML) — optional (controlled value)
              <br />
              • defaultValue — string (HTML) — optional (default &apos;&apos;)
              <br />
              • onChange — (html: string) =&gt; void — optional
              <br />
              • label — string — optional
              <br />
              • placeholder — string — optional
              <br />
              • minHeight — number | string — optional (default 240)
              <br />
              • compact — boolean — optional (default false)
              <br />
              • disabled — boolean — optional (default false)
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { SpeechEnhancedHtmlEditor } from '@/components/advanced/speech';

const [body, setBody] = useState('<p>Draft...</p>');

<SpeechEnhancedHtmlEditor
  label="Article body"
  value={body}
  onChange={setBody}
  minHeight={240}
/>`}
            </Code>
          </Stack>

          <Alert variant="light" color="grape" title="Needs to run live">
            <Text size="xs">
              Mounts the rich HtmlEditor plus <Code>useTextToSpeech()</Code> and{' '}
              <Code>useSpeechRecognition()</Code>, and reads the window selection when speaking
              (browser microphone / speech APIs). Not rendered live here — doc only.
            </Text>
          </Alert>
        </Stack>
      </Card>

      {/* SpeechCommandBar */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start">
              <Title order={4}>SpeechCommandBar</Title>
              <Badge color="grape" variant="light">
                browser microphone / speech APIs
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              A standalone voice control bar (no input field) to attach to any external text state:
              language/voice pickers plus Speak / Pause / Stop / Dictate. Appends dictated text via
              the <Code>onTextChange</Code> callback.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • text — string — required (the text Speak reads; also the base for appended dictation)
              <br />
              • onTextChange — (next: string) =&gt; void — optional (receives appended transcripts)
              <br />
              • compact — boolean — optional (default false)
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`import { SpeechCommandBar } from '@/components/advanced/speech';

const [text, setText] = useState('Read this aloud, or dictate to append.');

<SpeechCommandBar
  text={text}
  onTextChange={setText}
/>`}
            </Code>
          </Stack>

          <Alert variant="light" color="grape" title="Needs to run live">
            <Text size="xs">
              Calls <Code>useTextToSpeech()</Code> and <Code>useSpeechRecognition()</Code> on mount
              (browser microphone / speech APIs). Not rendered live here — doc only.
            </Text>
          </Alert>
        </Stack>
      </Card>

      <Divider label="Supporting APIs" labelPosition="center" />

      {/* Hooks + engine router (non-rendered reference) */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Hooks &amp; Engine Router</Title>
            <Text size="sm" c="dimmed">
              The components above are thin UIs over these primitives. Use the hooks directly to
              build a custom voice surface, and use the engine router to choose where speech is
              processed (with automatic fallback to the built-in browser engine).
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>useTextToSpeech()</Text>
              <Text size="xs" c="dimmed">
                Returns <Code>{`{ supported, speaking, paused, availableVoices, preferences, speak, stop, pause, resume, setVoice, setRate, setLanguage }`}</Code>.
                Guards every call behind a <Code>supported</Code> capability check.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>useSpeechRecognition(options?)</Text>
              <Text size="xs" c="dimmed">
                Returns <Code>{`{ supported, listening, transcript, error, preferences, start, stop, toggle, setLanguage, setContinuous, setInterimResults }`}</Code>.
                Builds a <Code>SpeechRecognition</Code> instance on mount.
              </Text>
            </Stack>
          </SimpleGrid>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Engine selection:</Text>
            <Code block language="tsx">
{`import {
  SPEECH_ENGINE_OPTIONS,   // Select data: Auto, Voicegain, IBM, Google, Whisper, Browser
  getSpeechEnginePreference,
  setSpeechEnginePreference,
  resolveSpeechEngine,     // async ('tts' | 'stt', key?) => ResolvedSpeechEngine
} from '@/components/advanced/speech';

setSpeechEnginePreference('auto');
const { engine, fallbackToBrowser } = await resolveSpeechEngine('tts');`}
            </Code>
            <Text size="xs" c="dimmed">
              <Code>SpeechEngineKey</Code> = &apos;auto&apos; | &apos;voicegain&apos; | &apos;ibm&apos; | &apos;google&apos; |
              &apos;whisper&apos; | &apos;browser&apos;. External engines require backend credentials; when
              none are available (or the device is offline) the router falls back to the browser
              engine.
            </Text>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
